import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import { supabase } from '../../services/supabase';
import { LiveGame, League, Player, LiveGameStatus } from '../../types/models';
import LiveGameSetup from './LiveGameSetup';
import LiveGameManagement from './LiveGameManagement';

const LiveGamesAdmin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId?: string }>();
  const [liveGames, setLiveGames] = useState<LiveGame[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [judges, setJudges] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<LiveGame | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // Check if we need to refresh the live games list
  const locationState = location.state as { refresh?: boolean } | null;

  // Determine which view to show based on the route
  const isSetupView = location.pathname.includes('/live-game-setup/');
  const isManagementView = location.pathname.includes('/live-game-management/');

  // Form state
  const [formData, setFormData] = useState({
    league_id: '',
    session_num: 1,
    number_in_session: 1,
    judge_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchLiveGames();
    fetchLeagues();
    fetchJudges();
  }, []);
  
  // If we navigate back from a game management page with refresh flag, refresh the list
  useEffect(() => {
    if (locationState?.refresh) {
      fetchLiveGames();
      // Clear the state to prevent unnecessary refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [locationState]);

  const fetchLiveGames = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('live_games')
        .select(`
          *,
          league:league_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLiveGames((data || []) as LiveGame[]);
    } catch (error: any) {
      console.error('Error fetching live games:', error.message);
      showSnackbar('Failed to load live games', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagues = async () => {
    try {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setLeagues(data || []);
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
    }
  };

  const fetchJudges = async () => {
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('is_judge', true)
        .order('nickname', { ascending: true });

      if (error) throw error;
      setJudges(data || []);
    } catch (error: any) {
      console.error('Error fetching judges:', error.message);
    }
  };

  const handleAddGame = () => {
    setSelectedGame(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEditGame = (game: LiveGame) => {
    setSelectedGame(game);
    setFormData({
      league_id: game.league_id,
      session_num: game.session_num,
      number_in_session: game.number_in_session,
      judge_id: game.judge_id,
      notes: game.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteGame = (game: LiveGame) => {
    setSelectedGame(game);
    setDeleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      league_id: '',
      session_num: 1,
      number_in_session: 1,
      judge_id: '',
      notes: ''
    });
  };

  const validateForm = () => {
    if (!formData.league_id) return false;
    if (!formData.judge_id) return false;
    if (formData.session_num < 1) return false;
    if (formData.number_in_session < 1) return false;
    return true;
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare form data for submission
      const gameData = {
        league_id: formData.league_id,
        session_num: formData.session_num,
        number_in_session: formData.number_in_session,
        judge_id: formData.judge_id,
        status: 'PENDING' as LiveGameStatus,
        notes: formData.notes || null
      };

      let response;
      
      if (selectedGame) {
        // Update existing game
        response = await supabase
          .from('live_games')
          .update(gameData)
          .eq('id', selectedGame.id)
          .select();
      } else {
        // Create new game
        response = await supabase
          .from('live_games')
          .insert([gameData])
          .select();
      }

      const { data, error } = response;

      if (error) throw error;

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchLiveGames();
      showSnackbar(
        selectedGame ? 'Live game updated successfully' : 'Live game created successfully', 
        'success'
      );

      // If it's a new game, redirect to setup page
      if (!selectedGame && data && data.length > 0) {
        navigate(`/admin/live-game-setup/${data[0].id}`);
      }

    } catch (error: any) {
      console.error('Error saving live game:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedGame) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('live_games')
        .delete()
        .eq('id', selectedGame.id);

      if (error) throw error;

      setDeleteDialogOpen(false);
      fetchLiveGames();
      showSnackbar('Live game deleted successfully', 'success');
    } catch (error: any) {
      console.error('Error deleting live game:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: LiveGameStatus) => {
    switch (status) {
      case 'PENDING': return 'default';
      case 'ACTIVE': return 'success';
      case 'COMPLETED': return 'info';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const navigateToSetup = (gameId: string) => {
    navigate(`/admin/live-game-setup/${gameId}`);
  };

  const navigateToManagement = (gameId: string) => {
    navigate(`/admin/live-game-management/${gameId}`);
  };
  
  const openOverlay = (gameId: string) => {
    window.open(`/overlay/${gameId}`, '_blank');
  };

  // Render the setup view if we're on that route
  if (isSetupView && gameId) {
    return <LiveGameSetup />;
  }

  // Render the management view if we're on that route
  if (isManagementView && gameId) {
    return <LiveGameManagement />;
  }

  // Otherwise, render the default list view
  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Live Games Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddGame}
          >
            Add Live Game
          </Button>
        </Box>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {loading && liveGames.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : liveGames.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
              No live games found. Create one to get started.
            </Typography>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                    <TableCell>League</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Judge</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveGames.map((game) => (
                    <TableRow key={game.id}>
                      <TableCell>{game.league?.name || 'Unknown'}</TableCell>
                      <TableCell>{`Session ${game.session_num}, Game ${game.number_in_session}`}</TableCell>
                      <TableCell>{game.judge?.nickname || 'Unknown'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={game.status} 
                          size="small" 
                          color={getStatusColor(game.status as LiveGameStatus)} 
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(game.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {game.status === 'PENDING' && (
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => navigateToSetup(game.id)}
                              title="Setup Players"
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          )}
                          {game.status === 'ACTIVE' && (
                            <>
                              <IconButton 
                                size="small" 
                                color="success" 
                                onClick={() => navigateToManagement(game.id)}
                                title="Manage Game"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="info" 
                                onClick={() => openOverlay(game.id)}
                                title="Open Overlay"
                              >
                                <ScreenShareIcon />
                              </IconButton>
                            </>
                          )}
                          {game.status === 'COMPLETED' && (
                            <IconButton 
                              size="small" 
                              color="info" 
                              onClick={() => openOverlay(game.id)}
                              title="Open Overlay"
                            >
                              <ScreenShareIcon />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleEditGame(game)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteGame(game)}
                            disabled={game.status === 'ACTIVE'}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedGame ? 'Edit Live Game' : 'Add Live Game'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="league-label">League *</InputLabel>
              <Select
                labelId="league-label"
                id="league_id"
                name="league_id"
                value={formData.league_id}
                label="League *"
                onChange={handleSelectChange}
                required
              >
                {leagues.map((league) => (
                  <MenuItem key={league.id} value={league.id}>
                    {league.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Session Number *"
                type="number"
                name="session_num"
                value={formData.session_num}
                onChange={handleInputChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Game Number *"
                type="number"
                name="number_in_session"
                value={formData.number_in_session}
                onChange={handleInputChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel id="judge-label">Judge *</InputLabel>
              <Select
                labelId="judge-label"
                id="judge_id"
                name="judge_id"
                value={formData.judge_id}
                label="Judge *"
                onChange={handleSelectChange}
                required
              >
                {judges.map((judge) => (
                  <MenuItem key={judge.id} value={judge.id}>
                    {judge.nickname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading || !validateForm()}
          >
            {loading ? <CircularProgress size={24} /> : selectedGame ? 'Update' : 'Create & Setup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this live game?
            {selectedGame?.status === 'COMPLETED' && 
              ' This game is completed and deleting it will remove all its associated data.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LiveGamesAdmin;